/**
 * Member-specific error copy. A failed LOAD on any Members screen renders through the
 * shared `ScreenError` (DNKADM-0440) with {@link MEMBERS_FORBIDDEN_MESSAGE} and
 * {@link MEMBERS_LOAD_FAILED_MESSAGE}; this file adds only the member ACTIONS' own codes
 * (erasure, credits, lots, membership, guests, invitations, …) on top of it.
 */
import { ApiError } from '@/api/errors';
import { moneyRefusalMessage } from '@/api/moneyRefusal';
import { serverMessage } from '@/api/serverMessage';
import { v2ErrorCode } from '@/api/v2Errors';
import { roleLimitedMessage } from '@/components/accessCopy';
import { authRefusalMessage } from './unconfirmedOutcome';

/** The Members screens' own "why and who to ask" for a `403`. */
export const MEMBERS_FORBIDDEN_MESSAGE = roleLimitedMessage('This screen', 'OWNER or ADMIN');

/** The Members screens' line for a `5xx`/network load failure. */
export const MEMBERS_LOAD_FAILED_MESSAGE = 'The members data could not be loaded. Please try again.';

/**
 * Inline view for a failed member ERASURE (DNKADM-0203). Distinguishes the two
 * "blocked" concepts that must NEVER be conflated:
 *  - `409 ERASURE_BLOCKED_LAST_OWNER` → `lastOwner:true`: a synchronous refusal,
 *    NOTHING erased; the member is the tenant's last active owner. The caller keeps
 *    the member and points to the `platform-members` role-change surface. (This is the
 *    OPPOSITE of the `200 status:'blocked'` outcome, which means the member IS gone.)
 *  - `502` → retryable "Temporary error — please try again." (keep the dialog open).
 *  - `500` → "Something went wrong. Contact support." (no auto-retry).
 *  - `403`/`401` → standard auth handling.
 * Any server-supplied text is brand-scrubbed via {@link serverMessage}. Note `502`/`500`
 * both map to the coarse `UNKNOWN` code, so they are branched on `error.status`.
 */
export interface ErasureErrorView {
  /** True only for the `409 ERASURE_BLOCKED_LAST_OWNER` refusal — render the role-change pointer. */
  lastOwner: boolean;
  /** Short, brand-scrubbed message to render inline in the confirm dialog. */
  message: string;
}

export function toErasureErrorView(error: unknown): ErasureErrorView {
  if (v2ErrorCode(error) === 'ERASURE_BLOCKED_LAST_OWNER') {
    return {
      lastOwner: true,
      message:
        'This member is the tenant’s last owner and can’t be deleted. Assign the owner ' +
        'role to another active member first, then try again.',
    };
  }
  if (error instanceof ApiError) {
    const msg = serverMessage(error);
    switch (error.code) {
      case 'FORBIDDEN':
        return { lastOwner: false, message: 'You’re not authorized to delete this member.' };
      case 'SESSION_EXPIRED':
        return {
          lastOwner: false,
          message: 'Your session expired. Please sign in again to continue.',
        };
      default:
        break;
    }
    // A 409 without the specific business code is still a refusal — nothing erased.
    if (error.status === 409) {
      return {
        lastOwner: false,
        message: msg ?? 'This member can’t be deleted right now. Please refresh and try again.',
      };
    }
    if (error.status === 502) {
      return { lastOwner: false, message: 'Temporary error — please try again.' };
    }
    if (error.status === 500) {
      return { lastOwner: false, message: 'Something went wrong. Contact support.' };
    }
    if (msg) return { lastOwner: false, message: msg };
  }
  return { lastOwner: false, message: 'The member could not be deleted. Please try again.' };
}

/**
 * Inline view for a failed Users-roster **CRM contact resync** (DNKADM-0204 — the
 * heal of an orphaned, contact-less login). Two flags drive the caller:
 *  - `retryable` — a `502 UPSTREAM_UNAVAILABLE`: the CRM/sign-in service was
 *    unreachable and the stored id is UNCHANGED, so the same click is safe to repeat.
 *    The row must NOT be relabelled as having no contact.
 *  - `refreshList` — a `404 MEMBER_NOT_FOUND`: the row is stale, so reload the roster.
 * `403`/`401` are ordinary auth refusals with no effect (the action is admin-gated).
 * Server-supplied text is brand-scrubbed via {@link serverMessage}.
 */
export interface ResyncContactErrorView {
  /** True only for the transient `502` — offer a retry affordance. */
  retryable: boolean;
  /** True when the roster is stale and should be refetched. */
  refreshList: boolean;
  /** Short, brand-scrubbed message to render against the action. */
  message: string;
}

export function toResyncContactErrorView(error: unknown): ResyncContactErrorView {
  const status = error instanceof ApiError ? error.status : 0;
  if (status === 502 || v2ErrorCode(error) === 'UPSTREAM_UNAVAILABLE') {
    return {
      retryable: true,
      refreshList: false,
      message: 'Couldn’t reach the CRM service — try again.',
    };
  }
  if (error instanceof ApiError) {
    switch (error.code) {
      case 'NOT_FOUND':
        return {
          retryable: false,
          refreshList: true,
          message: 'That user was not found — the roster has been refreshed.',
        };
      case 'FORBIDDEN':
        return {
          retryable: false,
          refreshList: false,
          message: 'You’re not authorized to resync this user’s CRM contact.',
        };
      case 'SESSION_EXPIRED':
        return {
          retryable: false,
          refreshList: false,
          message: 'Your session expired. Please sign in again to continue.',
        };
      default:
        break;
    }
  }
  return {
    retryable: false,
    refreshList: false,
    message: serverMessage(error) ?? 'The CRM contact could not be resynced. Please try again.',
  };
}

/**
 * Inline view for a failed Prospects list/refresh load (DNKADM-0205 U2 — the CRM-only
 * contacts tab). Distinguishes the one transient failure that is safe to repeat from
 * everything else:
 *  - `retryable` — a `502 UPSTREAM_UNAVAILABLE`: the CRM/upstream service was
 *    unreachable, so the SAME load is safe to retry. This must NEVER be read as
 *    "no CRM connected" — that is the `NOT_CONFIGURED` `200`, handled as data, not an
 *    error.
 *  - Everything else (`403`/`404`/`401`/unknown) is non-retryable; the caller raises it
 *    as an error toast (a `404` tenant-config issue should not happen for your own
 *    tenant).
 * Server-supplied text is brand-scrubbed via {@link serverMessage}; the fixed copy is
 * already brand-neutral (says "CRM", never the vendor name).
 */
export interface ProspectsErrorView {
  /** True only for the transient `502` — offer a retry affordance, keep it inline. */
  retryable: boolean;
  /** Short, brand-scrubbed message. */
  message: string;
}

export function toProspectsErrorView(error: unknown): ProspectsErrorView {
  const status = error instanceof ApiError ? error.status : 0;
  if (status === 502 || v2ErrorCode(error) === 'UPSTREAM_UNAVAILABLE') {
    return { retryable: true, message: 'Couldn’t reach the CRM service — try again.' };
  }
  if (error instanceof ApiError) {
    switch (error.code) {
      case 'FORBIDDEN':
        return { retryable: false, message: 'You’re not authorized to view prospects.' };
      case 'SESSION_EXPIRED':
        return {
          retryable: false,
          message: 'Your session expired. Please sign in again to continue.',
        };
      case 'NOT_FOUND':
        return {
          retryable: false,
          message:
            serverMessage(error) ??
            'Prospects aren’t available for this tenant. Please contact support.',
        };
      default:
        break;
    }
  }
  return {
    retryable: false,
    message: serverMessage(error) ?? 'Prospects could not be loaded. Please try again.',
  };
}

/**
 * Inline message for a failed credit adjustment (DNKADM-0002). Maps the typed
 * `ApiError.code` to the right staff-facing line:
 *  - `CONFLICT` (409) → insufficient balance for the debit;
 *  - `VALIDATION` (400) → blank reason / zero amount / unknown bucket (prefer the
 *    server's own message when present);
 *  - `FORBIDDEN` (403) → the member is outside the caller's tenant;
 *  - `SESSION_EXPIRED` (401) → re-auth (the shell handles the refresh/redirect).
 * Anything else falls back to the server message, then a generic line.
 */
export function toCreditAdjustErrorMessage(error: unknown): string {
  // DNKAPI-0670's money refusals (MANAGER_PIN_*, STAFF_NOT_ALLOWED) say what to do next.
  const refusal = moneyRefusalMessage(error);
  if (refusal !== null) return refusal;
  // DNKADM-0480 (F-255): a 401, or a 403 that names no reason, reads the shared money-dialog line.
  const authLine = authRefusalMessage(error);
  if (authLine !== null) return authLine;
  if (error instanceof ApiError) {
    const msg = serverMessage(error);
    switch (error.code) {
      case 'CONFLICT':
        return msg ?? 'Not enough credits in this bucket to debit that amount.';
      case 'VALIDATION':
        return msg ?? 'That adjustment isn’t valid — check the bucket, amount, and reason.';
      case 'FORBIDDEN':
        return 'You’re not authorized to adjust credits for this member.';
      case 'SESSION_EXPIRED':
        return 'Your session expired. Please sign in again to continue.';
      default:
        if (msg) return msg;
    }
  }
  return 'The adjustment could not be completed. Please try again.';
}

/**
 * Inline message for a failed per-lot **extend-expiry** op (DNKADM-0046 U2). Maps
 * the typed `ApiError.code` to the right staff-facing line per the 0103 table:
 *  - `VALIDATION` (400) → a past `expiresAt` or blank reason. The dialog already
 *    client-blocks both, so a server 400 is a fallback; "Pick a future date" is the
 *    expected cause (the reason field has its own non-blank gate). Prefer the
 *    server's own message when present.
 *  - `FORBIDDEN` (403) → the member is outside the caller's tenant / not OWNER/ADMIN.
 *  - `NOT_FOUND` (404) → the lot doesn't exist for this member's tenant — the
 *    breakdown is stale and should be reloaded.
 *  - `SESSION_EXPIRED` (401) → re-auth (the shell handles the refresh/redirect).
 * Anything else falls back to the server message, then a generic line.
 */
export function toLotExtendErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const msg = serverMessage(error);
    switch (error.code) {
      case 'VALIDATION':
        return msg ?? 'Pick a future date.';
      case 'FORBIDDEN':
        return 'You don’t have access to this member.';
      case 'NOT_FOUND':
        return 'That credit grant was not found — refresh the breakdown.';
      case 'SESSION_EXPIRED':
        return 'Your session expired. Please sign in again to continue.';
      default:
        if (msg) return msg;
    }
  }
  return 'The expiry change could not be completed. Please try again.';
}

/**
 * Inline message for a failed per-lot **reduce** op (DNKADM-0046 U3). Maps the typed
 * `ApiError.code` to the right staff-facing line per the 0103 table:
 *  - `VALIDATION` (400) → amount ≤ 0 or blank reason. The dialog already client-blocks
 *    both (amount ∈ [1, remaining], non-blank reason), so a server 400 is a fallback;
 *    "Enter a positive amount" is the expected cause. Prefer the server's own message
 *    when present.
 *  - `CONFLICT` (409) → amount > the lot's remaining. The dialog caps the amount at
 *    `remaining` client-side, so this is a fallback (e.g. a stale breakdown); the
 *    canned line **interpolates the lot's `remaining`** so the cap is explicit.
 *  - `FORBIDDEN` (403) → the member is outside the caller's tenant / not OWNER/ADMIN.
 *  - `NOT_FOUND` (404) → the lot doesn't exist for this member's tenant — the
 *    breakdown is stale and should be reloaded.
 *  - `SESSION_EXPIRED` (401) → re-auth (the shell handles the refresh/redirect).
 * Anything else falls back to the server message, then a generic line.
 */
export function toLotReduceErrorMessage(error: unknown, remaining: number): string {
  // DNKADM-0480 (F-255): a 401, or a 403 that names no reason, reads the shared money-dialog line.
  const authLine = authRefusalMessage(error);
  if (authLine !== null) return authLine;
  if (error instanceof ApiError) {
    const msg = serverMessage(error);
    switch (error.code) {
      case 'VALIDATION':
        return msg ?? 'Enter a positive amount.';
      case 'CONFLICT':
        return `You can remove at most ${remaining} from this grant.`;
      case 'FORBIDDEN':
        return 'You don’t have access to this member.';
      case 'NOT_FOUND':
        return 'That credit grant was not found — refresh the breakdown.';
      case 'SESSION_EXPIRED':
        return 'Your session expired. Please sign in again to continue.';
      default:
        if (msg) return msg;
    }
  }
  return 'The reduction could not be completed. Please try again.';
}

/**
 * Inline message for the batch lot-expiry endpoint used by the grouped
 * "Manage credits & expirations" card. The endpoint is atomic per bucket; on any
 * failure the caller keeps the existing loaded lots intact and surfaces this line.
 */
export function toLotsExpiryErrorMessage(error: unknown): string {
  // DNKADM-0480 (F-255): a 401, or a 403 that names no reason, reads the shared money-dialog line.
  const authLine = authRefusalMessage(error);
  if (authLine !== null) return authLine;
  if (error instanceof ApiError) {
    const msg = serverMessage(error);
    switch (error.code) {
      case 'VALIDATION':
        return msg ?? 'Pick a future expiry date.';
      case 'FORBIDDEN':
        return 'You don’t have access to this member.';
      case 'NOT_FOUND':
        return 'One of these credit grants was not found — reload the grants.';
      case 'SESSION_EXPIRED':
        return 'Your session expired. Please sign in again to continue.';
      default:
        if (msg) return msg;
    }
  }
  return 'The expiry change could not be completed. Please try again.';
}

/**
 * Inline message for a failed membership change (DNKADM-0004). Maps the typed
 * `ApiError.code` to the right staff-facing line:
 *  - `VALIDATION` (400) → blank tier / blank reason (prefer the server's message);
 *  - `FORBIDDEN` (403) → not OWNER/ADMIN, or the member is outside the caller's tenant;
 *  - `NOT_FOUND` (404) → the tenant has no active GoHighLevel integration;
 *  - `SESSION_EXPIRED` (401) → re-auth (the shell handles the refresh/redirect);
 *  - anything else → the forwarded GHL rejection. This is the LOAD-BEARING path: an
 *    off-list tier/status is rejected by the GHL `SINGLE_OPTIONS` field and forwarded
 *    with its upstream message, so we surface that message verbatim rather than
 *    assuming success. Falls back to a generic line only when no message is present.
 */
export function toMembershipUpdateErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const msg = serverMessage(error);
    switch (error.code) {
      case 'VALIDATION':
        return msg ?? 'That membership change isn’t valid — check the tier and reason.';
      case 'FORBIDDEN':
        return msg ?? 'You’re not authorized to change membership for this member.';
      case 'NOT_FOUND':
        return (
          msg ??
          'This tenant has no active Journix integration, so membership can’t be updated.'
        );
      case 'SESSION_EXPIRED':
        return 'Your session expired. Please sign in again to continue.';
      default:
        // Forwarded GHL rejection (e.g. an off-list tier/status) — surface upstream.
        if (msg) return msg;
    }
  }
  return 'The membership change could not be completed. Please try again.';
}

/**
 * Inline message for a failed guest moderation op (DNKADM-0068 U2 — archive /
 * unarchive / block / unblock). Maps the typed `ApiError.code` to the right
 * staff-facing line:
 *  - `NOT_FOUND` (404) → the (member, guest) relationship doesn't exist for this
 *    tenant (a foreign / stale `memberGuestId`) — the caller handles this case by
 *    refreshing the list, so this line is only a fallback if it ever renders inline.
 *  - `FORBIDDEN` (403) → not OWNER/ADMIN, or the member is outside the caller's tenant.
 *  - `SESSION_EXPIRED` (401) → re-auth (the shell handles the refresh/redirect).
 *  - anything else → the server message, then a generic line.
 */
export function toGuestModerationErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const msg = serverMessage(error);
    switch (error.code) {
      case 'NOT_FOUND':
        return 'That guest record was not found — refresh the list.';
      case 'FORBIDDEN':
        return 'You’re not authorized to moderate this member’s guests.';
      case 'SESSION_EXPIRED':
        return 'Your session expired. Please sign in again to continue.';
      default:
        if (msg) return msg;
    }
  }
  return 'The change could not be completed. Please try again.';
}

/**
 * Where the Add-Member create error renders (the real DNKAPI-0172 codes):
 *  - `'form'` — a request-shape rejection (`MEMBERSHIP_PLAN_NOT_FOUND` / `NOT_FOUND` bad
 *    bucket / `INVALID_REQUEST`): shown **inline** within the form (form-level here — the
 *    offending field isn't reliably identifiable from the code alone);
 *  - `'payment'` — `PROVIDER_NOT_CONFIGURED`: shown on the **payment block**;
 *  - `'toast'` — anything else (auth/network/upstream): raised as an error toast.
 *
 * A bare `404` with NO business code (endpoint not live — DNKAPI-0172 pre-ship) is the
 * forward-compat state and is handled by the dialog directly (a neutral "not available
 * yet" message), NOT routed through this classifier.
 */
export interface CreateMemberErrorView {
  scope: 'form' | 'payment' | 'toast';
  message: string;
}

/**
 * The real DNKAPI-0172 create codes that surface INLINE (form-level) — a bad plan/bucket
 * or a malformed request. `MEMBERSHIP_PLAN_NOT_FOUND`/`NOT_FOUND` arrive as a `404`,
 * `INVALID_REQUEST` as a `400`; all carry the code on `details.code`.
 */
const FORM_CODES = new Set([
  'MEMBERSHIP_PLAN_NOT_FOUND',
  'NOT_FOUND',
  'INVALID_REQUEST',
]);

/**
 * Classify a failed `createMember` (§D) into where the dialog renders it, on the real
 * shipped 0172 codes. A plan/bucket/shape rejection (`MEMBERSHIP_PLAN_NOT_FOUND` /
 * `NOT_FOUND` / `INVALID_REQUEST`) is form-level; `PROVIDER_NOT_CONFIGURED` is on the
 * payment block; a bare `400` VALIDATION with no business code still falls to the form;
 * everything else (FORBIDDEN / SESSION_EXPIRED / upstream / network) is a toast. Prefers
 * the server's own (brand-scrubbed) message, then a canned line.
 */
export function toCreateMemberErrorView(error: unknown): CreateMemberErrorView {
  const msg = serverMessage(error);
  const code = v2ErrorCode(error);
  if (code === 'PROVIDER_NOT_CONFIGURED') {
    return {
      scope: 'payment',
      message:
        msg ??
        'Payments aren’t set up for this location yet, so this payment can’t be recorded. ' +
          'No member was created.',
    };
  }
  if (FORM_CODES.has(code ?? '')) {
    return {
      scope: 'form',
      message:
        msg ??
        'Something here isn’t valid — check the plan and the credit buckets, then try again.',
    };
  }
  if (error instanceof ApiError) {
    switch (error.code) {
      case 'VALIDATION':
        return {
          scope: 'form',
          message:
            msg ??
            'Something here isn’t valid — check the plan, the credit amounts, and the payment.',
        };
      case 'FORBIDDEN':
        return {
          scope: 'toast',
          message: 'You’re not authorized to add members for this location.',
        };
      case 'SESSION_EXPIRED':
        return {
          scope: 'toast',
          message: 'Your session expired. Please sign in again to continue.',
        };
      default:
        return { scope: 'toast', message: msg ?? 'The member could not be added. Please try again.' };
    }
  }
  return { scope: 'toast', message: 'The member could not be added. Please try again.' };
}

/**
 * Inline message for a failed member profile-values save (DNKADM-0104 / DNKAPI-0137).
 * Maps the error set to the right staff-facing line:
 *  - `FIELD_NOT_WRITABLE` (a 400 business code) → a field the admin tried to write is
 *    read-only upstream. The editor already gates submission on the value's `writable`,
 *    so this is a fallback (e.g. a stale read) — prompt a reload. Prefer the server's
 *    own (brand-scrubbed) message when present.
 *  - `VALIDATION` (400, incl. `INVALID_REQUEST`) → a value isn't valid for its field.
 *  - `NOT_FOUND` (404) → the member no longer exists — the not-found path (reload).
 *  - `FORBIDDEN` (403) → not OWNER/ADMIN, or the member is outside the caller's tenant.
 *  - `SESSION_EXPIRED` (401) → re-auth (the shell handles the refresh/redirect).
 * Anything else falls back to the server message, then a generic line.
 */
export function toProfileSaveErrorMessage(error: unknown): string {
  const msg = serverMessage(error);
  if (v2ErrorCode(error) === 'FIELD_NOT_WRITABLE') {
    return msg ?? 'One of these fields can’t be edited. Reload the profile and try again.';
  }
  if (error instanceof ApiError) {
    switch (error.code) {
      case 'VALIDATION':
        return msg ?? 'Some of these values aren’t valid — check the fields and try again.';
      case 'NOT_FOUND':
        return 'This member was not found — they may have been removed. Reload the page.';
      case 'FORBIDDEN':
        return 'You’re not authorized to edit this member’s profile.';
      case 'SESSION_EXPIRED':
        return 'Your session expired. Please sign in again to continue.';
      default:
        if (msg) return msg;
    }
  }
  return 'The profile could not be saved. Please try again.';
}

/**
 * Inline message for a failed coach assign/unassign (DNKADM-0301). Maps the typed
 * `ApiError.code` to the right staff-facing line:
 *  - `MEMBER_STAFF_NOT_FOUND` (404) → stale/soft-removed picker row;
 *  - `FORBIDDEN`/other `NOT_FOUND` → the member is outside the caller's tenant;
 *  - network/5xx → retryable generic copy;
 *  - `SESSION_EXPIRED` (401) → re-auth (the shell handles the refresh/redirect);
 *  - anything else → the server message, then a generic line.
 */
export function toStaffAssignmentErrorMessage(error: unknown): string {
  const code = v2ErrorCode(error);
  if (code === 'MEMBER_STAFF_NOT_FOUND') {
    return 'That staffer is no longer available. Pick another coach.';
  }
  if (error instanceof ApiError) {
    const msg = serverMessage(error);
    if (error.code === 'NETWORK_ERROR' || error.status >= 500) {
      return 'The coach assignment could not be completed. Please try again.';
    }
    switch (error.code) {
      case 'VALIDATION':
        return msg ?? 'That staff member can’t be assigned — they may not be a valid coach.';
      case 'FORBIDDEN':
        return 'This member was not found in this tenant.';
      case 'NOT_FOUND':
        return 'This member was not found in this tenant.';
      case 'SESSION_EXPIRED':
        return 'Your session expired. Please sign in again to continue.';
      default:
        if (msg) return msg;
    }
  }
  return 'The coach assignment could not be completed. Please try again.';
}

/**
 * Inline message for a failed home-location set/change (DNKADM-0239). Maps the
 * typed `ApiError.code`:
 *  - `VALIDATION` (400 — the server's `HOME_LOCATION_INVALID`: the target is not a
 *    tenant-scoped, active location; no upsert happened) → prefer the server's own
 *    (brand-scrubbed) message;
 *  - `FORBIDDEN` (403) → not OWNER/ADMIN, or the member is outside the tenant;
 *  - `SESSION_EXPIRED` (401) → re-auth (the shell handles the refresh/redirect);
 *  - anything else → the server message, then a generic line.
 */
export function toHomeLocationErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const msg = serverMessage(error);
    switch (error.code) {
      case 'VALIDATION':
        return msg ?? 'That location can’t be set as the home — it may be inactive or not part of this facility.';
      case 'FORBIDDEN':
        return 'You’re not authorized to change this member’s home location.';
      case 'SESSION_EXPIRED':
        return 'Your session expired. Please sign in again to continue.';
      default:
        if (msg) return msg;
    }
  }
  return 'The home location could not be changed. Please try again.';
}

/**
 * Inline outcome of a Users-screen USER-invitation WRITE (DNKADM-0168 — invite /
 * cancel / resend). Distinct from the whole-screen load state (the shared
 * `ScreenError`): a write failure is surfaced inline against the affordance. Mirrors the
 * platform-members write view — the fine-grained v2 business `code`
 * (`ApiError.details.code`, via {@link v2ErrorCode}) drives the precise line, then
 * the HTTP status; `refreshList` signals the target moved on under the write
 * (`INVITATION_ALREADY_ACCEPTED`) so the caller reloads rather than hard-errors.
 */
export interface UserInviteErrorView {
  /** The v2 business `code` (`ApiError.details.code`) when present, else undefined. */
  code: string | undefined;
  /** HTTP status of the failure (`0` for a non-`ApiError`). */
  status: number;
  /** Short, brand-scrubbed message to render inline against the affordance. */
  message: string;
  /** True when the caller should refresh the invitation list rather than hard-error. */
  refreshList: boolean;
}

function userInviteView(
  status: number,
  code: string | undefined,
  message: string,
  refreshList = false,
): UserInviteErrorView {
  return { code, status, message, refreshList };
}

/**
 * Classify a thrown error from a USER-invitation WRITE into an inline view
 * (DNKADM-0168 §Error table). Branches on the v2 business `code` first, then the
 * HTTP status; server-supplied text is brand-scrubbed via {@link serverMessage} where
 * it is shown (the fixed known-code copy is already brand-neutral). Unknown codes
 * degrade to the status branch, then the server message, then a generic line — never
 * a throw.
 */
export function toUserInviteErrorView(error: unknown): UserInviteErrorView {
  const status = error instanceof ApiError ? error.status : 0;
  const code = v2ErrorCode(error);

  switch (code) {
    case 'EMAIL_REQUIRED':
      return userInviteView(status, code, 'Enter an email address to send an invitation.');
    case 'VALUE_NOT_ALLOWED':
    case 'INVALID_REQUEST':
      return userInviteView(
        status,
        code,
        serverMessage(error) ?? 'Please check the details and try again.',
      );
    case 'ALREADY_ACTIVE_MEMBER':
      return userInviteView(status, code, 'This person is already a member of this tenant.');
    case 'INVITATION_ALREADY_PENDING':
      return userInviteView(status, code, 'An invitation is already pending for this email.');
    case 'INVITATION_ALREADY_ACCEPTED':
      return userInviteView(
        status,
        code,
        'This invitation was already accepted — refreshing the list.',
        true,
      );
    case 'INVITATION_NOT_FOUND':
      return userInviteView(status, code, 'That invitation was not found — refresh and try again.');
    case 'UPSTREAM_UNAVAILABLE':
      return userInviteView(
        status,
        code,
        'The sign-in service is unavailable — please try again later.',
      );
    default:
      break;
  }

  switch (status) {
    case 401:
      return userInviteView(status, code, 'Your session expired. Please sign in again.');
    case 403:
      return userInviteView(status, code, 'You don’t have permission to make this change.');
    case 404:
      return userInviteView(status, code, 'That invitation was not found — refresh and try again.');
    case 409:
      return userInviteView(
        status,
        code,
        serverMessage(error) ?? 'That change conflicts with the current state — refresh and try again.',
      );
    case 400:
      return userInviteView(
        status,
        code,
        serverMessage(error) ?? 'Please check the details and try again.',
      );
    case 502:
      return userInviteView(
        status,
        code,
        'The sign-in service is unavailable — please try again later.',
      );
    default:
      return userInviteView(
        status,
        code,
        serverMessage(error) ?? 'Something went wrong. Please try again.',
      );
  }
}

/**
 * Inline view for a failed verification-email resend (DNKADM-0444 / DNKAPI-0650). The
 * dialog stays open on every failure:
 *  - `429 VERIFICATION_RESEND_RATE_LIMITED` → "try again in a minute", NOT retryable
 *    from the dialog (an immediate Retry would just be throttled again);
 *  - anything else (409 / 502 / network / unknown) → a generic line with a Retry.
 * Server text is deliberately not forwarded: the copy here is the contract.
 */
export interface ResendVerificationErrorView {
  /** True when the inline Alert should offer Retry. */
  retryable: boolean;
  message: string;
}

export function toResendVerificationErrorView(error: unknown): ResendVerificationErrorView {
  const status = error instanceof ApiError ? error.status : 0;
  if (v2ErrorCode(error) === 'VERIFICATION_RESEND_RATE_LIMITED' || status === 429) {
    return { retryable: false, message: 'Too many attempts. Try again in a minute.' };
  }
  return { retryable: true, message: "Couldn't send the email. Try again." };
}
